/**
 * What the Computer page shows (slice 6c §5's table), from the computer's
 * state and who's looking: one state, its pill, and its words.
 */
import { shortTime } from "../chats/format";
import type { ComputerState } from "./api";
import { SIGN_IN_NOTE } from "./desktop-link";

export type PageState =
  /** No desktop here (E2B, the local computer), or not set up: nothing to show. */
  | { kind: "unavailable" }
  /** No computer at all: the crew answers without one (spike S-429). */
  | { kind: "none" }
  /** Awake, nobody holds it, no turn: the desktop, view only (95, 95d). */
  | { kind: "watching" }
  /** Asleep (95a): watching doesn't wake it, Take over does. */
  | { kind: "asleep" }
  /** Waking (95b): for the viewer's Take over (`mine`, Cancel), or for an ask. */
  | { kind: "waking"; mine: boolean; step: number }
  /** Held by the viewer (95c, 95e): the desktop in control. */
  | { kind: "yours"; waiting: number }
  /** A turn is claimed or running (95f): its work, view only. */
  | { kind: "working"; working: NonNullable<ComputerState["working"]> }
  /**
   * Someone else holds it: no stream, which would take theirs (§6). The
   * viewer's own handoff's hold is here too: it ends in its chat.
   */
  | { kind: "held"; name: string; mine: boolean; threadId: string | null };

const NOT_SET_UP = new Set(["setting_up", "waiting", "released", "error"]);

/** The wake's steps (95b), as the boards name them. A wake reports no steps: its status says. */
export const WAKE_STEPS = ["Starting the computer", "Restoring its files", "Opening the desktop"];

export function pageState(state: ComputerState | undefined, viewerId: string): PageState {
  if (state?.no_computer) return { kind: "none" };
  if (!state || !state.desktop || NOT_SET_UP.has(state.status)) return { kind: "unavailable" };
  const held = state.held;
  if (held && (held.user_id !== viewerId || held.handoff_thread_id)) {
    const mine = held.user_id === viewerId;
    return { kind: "held", name: held.name, mine, threadId: held.handoff_thread_id };
  }
  if (held) {
    return state.status === "ready"
      ? { kind: "yours", waiting: state.waiting }
      : { kind: "waking", mine: true, step: wakeStep(state.status) };
  }
  if (state.working) return { kind: "working", working: state.working };
  if (state.status === "ready") return { kind: "watching" };
  if (state.status === "waking") return { kind: "waking", mine: false, step: 1 };
  return { kind: "asleep" };
}

/** Which step a wake is on: asked (0), waking (1). */
const wakeStep = (status: ComputerState["status"]) => (status === "waking" ? 1 : 0);

/** The pill's words and dot (§5's table). */
export function pillOf(s: PageState): { text: string; dot: "moss" | "hint" | null; moss: boolean } {
  switch (s.kind) {
    case "watching":
      return { text: "LIVE · VIEW ONLY", dot: "moss", moss: false };
    case "working":
      return { text: "LIVE · CREW WORKING", dot: "moss", moss: false };
    case "yours":
      return { text: "YOU HAVE IT", dot: "moss", moss: true };
    case "held":
      return s.mine
        ? { text: "YOU HAVE IT", dot: "moss", moss: true }
        : { text: `${s.name.toUpperCase()} HAS IT`, dot: "hint", moss: false };
    case "asleep":
      return { text: "ASLEEP", dot: "hint", moss: false };
    case "waking":
      return { text: "WAKING", dot: "hint", moss: false };
    case "unavailable":
      return { text: "NOT AVAILABLE", dot: null, moss: false };
    case "none":
      return { text: "NO COMPUTER", dot: null, moss: false };
  }
}

/** "8:10" today, "yesterday", "Mon", "Sep 3": when it fell asleep, or null when unknown. */
export function asleepSince(state: ComputerState | undefined, timeZone: string): string | null {
  if (!state?.asleep_at) return null;
  const at = shortTime(state.asleep_at, timeZone);
  return at === "Yesterday" ? "yesterday" : at || null;
}

/** The footer's line on the right (boards 95–95c). */
export function footRight(s: PageState, since: string | null): string | null {
  switch (s.kind) {
    case "watching":
      return "Awake · nothing running";
    case "asleep":
      return since ? `Asleep since ${since}` : "Asleep";
    case "waking":
      return s.mine ? "Asks wait while you have it" : null;
    case "yours":
      return s.waiting > 0
        ? `Asks wait while you have it · ${s.waiting} waiting`
        : "Asks wait while you have it";
    default:
      return null;
  }
}

/** "Chief of Staff is working in “Exercise bikes under $1000”." (95f) */
export function workingLine(w: NonNullable<ComputerState["working"]>): string {
  const where = w.title ? ` in “${w.title}”` : "";
  return `${w.agent} is working${where}. You can take over when it's done; to stop it, open that chat.`;
}

/** The dark area's words when there's no stream (95a, 95b). */
export function stillWords(
  s: PageState,
  since: string | null,
  /** Take over is here; not on a phone for a lean box (S-455). */
  takeHere = true,
): { title: string; line: string } {
  if (s.kind === "asleep") {
    const at = since ? `Since ${since}. ` : "";
    const wakes = takeHere
      ? "Watching doesn't wake it. Take over does, in about a minute."
      : "It wakes when the crew has something to do.";
    return { title: "The computer is asleep", line: `${at}${wakes}` };
  }
  if (s.kind === "waking") {
    const line = since ? `About a minute. It's been asleep since ${since}.` : "About a minute.";
    return { title: "Waking the computer", line };
  }
  if (s.kind === "held") return heldWords(s);
  if (s.kind === "none") {
    return {
      title: "This workspace has no computer yet",
      line: "Your crew answers chats without one. The browser and the computer's screen come later.",
    };
  }
  return {
    title: "The computer's screen isn't available",
    line: "It shows here once the computer is set up and awake.",
  };
}

const heldWords = (s: Extract<PageState, { kind: "held" }>) =>
  s.mine
    ? { title: "You have the computer", line: "You took it for a handoff: finish it in its chat." }
    : { title: `${s.name} has the computer`, line: `You can take over when ${s.name} is done.` };

/** What the desktop area shows: the stream in control, view only, or a still. */
export function screenOf(s: PageState): "control" | "watch" | "still" {
  if (s.kind === "yours") return "control";
  return s.kind === "watching" || s.kind === "working" ? "watch" : "still";
}

/** The page holds the computer: kept every 5 minutes, and let go on leaving (§5). */
export const holdsIt = (s: PageState) => s.kind === "yours" || (s.kind === "waking" && s.mine);

/**
 * The caller holds it from this page (not a handoff's take), whatever the
 * page shows: a wake that failed shows "unavailable" with the hold still set,
 * so leaving or the page itself lets it go (Codex on #375).
 */
export const pageHold = (state: ComputerState | undefined, me: string) =>
  state?.held?.user_id === me && state.held.handoff_thread_id === null;

/** When someone's hold ends with no event to say so. */
export const heldUntil = (state: ComputerState | undefined) => state?.held?.until ?? null;

export type NoteIcon = "eye" | "moon" | "clock" | "lock";

/** The footer's line (95–95f), its icon, and the chat it links to. */
export type Note = { icon: NoteIcon; text: string; chat: string | null };

const note = (icon: NoteIcon, text: string, chat: string | null = null): Note => ({
  icon,
  text,
  chat,
});

const NOTES: Partial<Record<PageState["kind"], (s: PageState, phone: boolean) => Note | null>> = {
  watching: (_, phone) =>
    note(
      "eye",
      `View only. Take over to ${phone ? "tap" : "click"} and type; the crew's asks wait while you have it.`,
    ),
  asleep: () => note("moon", "It wakes on its own when the crew has something to do."),
  working: (s) =>
    s.kind === "working" ? note("clock", workingLine(s.working), s.working.thread_id) : null,
  yours: () => note("lock", SIGN_IN_NOTE),
  waking: (s) =>
    s.kind === "waking" && s.mine
      ? note("lock", SIGN_IN_NOTE)
      : note("clock", "Waking for the crew's next ask."),
  held: (s) => (s.kind === "held" ? heldNote(s) : null),
};

const heldNote = (s: Extract<PageState, { kind: "held" }>) =>
  s.mine
    ? s.threadId
      ? note("lock", "Finish the handoff in its chat.", s.threadId)
      : null
    : note("lock", `Asks wait while ${s.name} has it.`);

export const noteOf = (s: PageState, phone: boolean): Note | null =>
  NOTES[s.kind]?.(s, phone) ?? null;

/** What a phone says instead of Take over where it can't be done there (S-455). */
export const COMPUTER_ONLY =
  "Take over works on a computer for now. Open Crew there to sign in or finish a check.";

/**
 * On a phone, a computer whose Take over needs a bigger screen (a pi
 * computer's lean box, S-455): no Take over, and the note says where to.
 */
export function onPhone(
  action: ActionKind | null,
  phoneControl: boolean | undefined,
): { action: ActionKind | null; note: Note | null } {
  if (phoneControl !== false || action?.does !== "take") return { action, note: null };
  return { action: null, note: note("lock", COMPUTER_ONLY) };
}

/** The state's one action (§5): Take over, Done, Cancel, or none. */
export type ActionKind = {
  label: "Take over" | "Done" | "Cancel";
  does: "take" | "release";
  /** Crew working: Take over shows, and waits for the reply to end. */
  disabled: boolean;
  outline: boolean;
};

const TAKE: ActionKind = { label: "Take over", does: "take", disabled: false, outline: false };

const ACTIONS: Partial<Record<PageState["kind"], (s: PageState) => ActionKind>> = {
  watching: () => TAKE,
  asleep: () => TAKE,
  working: () => ({ ...TAKE, disabled: true }),
  yours: () => ({ label: "Done", does: "release", disabled: false, outline: false }),
  waking: (s) =>
    s.kind === "waking" && s.mine
      ? { label: "Cancel", does: "release", disabled: false, outline: true }
      : TAKE,
};

export const actionOf = (s: PageState): ActionKind | null => ACTIONS[s.kind]?.(s) ?? null;

/** A wake step against the one it's on: done, on it, or still to come. */
export const stepState = (i: number, step: number) =>
  i < step ? "done" : i === step ? "now" : "later";
